"use client"

import { PackageCard } from "@/features/client/components/packages/PackageCard"
import { NoPackagesFound } from "./NoPackageFound"
import { Button } from "@/shared/components/ui/button"
import { RefreshCw } from "lucide-react"
import type { TravelPackage, Region } from "@/shared/types/packages"

interface PackagesSectionProps {
  filteredPackages: TravelPackage[]
  selectedRegion: Region | null
  onRefresh?: () => void
  refreshing?: boolean
}

export function PackagesSection({ filteredPackages, selectedRegion, onRefresh, refreshing }: PackagesSectionProps) {
  return (
    <section className="max-w-7xl mx-auto container px-4 py-12 sm:py-16 mb-8">
      <div className="mb-10 sm:mb-12 animate-in fade-in duration-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            {selectedRegion ? `${selectedRegion.charAt(0) + selectedRegion.slice(1).toLowerCase()} Destinations` : 'All Destinations'}
          </h2>
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full">
              <span className="text-2xl font-bold">{filteredPackages.length}</span>
              <span className="text-sm font-semibold">{filteredPackages.length === 1 ? 'Package' : 'Packages'}</span>
            </div>
            {onRefresh && (
              <Button
                onClick={onRefresh}
                disabled={refreshing}
                variant="outline"
                size="sm"
                className="hover:bg-orange-50 hover:border-orange-300"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            )}
          </div>
        </div>
        <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
          {selectedRegion 
            ? `Discover amazing destinations across ${selectedRegion.toLowerCase()}` 
            : `Browse through our complete collection of travel packages worldwide`}
        </p>
      </div>

      {filteredPackages.length > 0 ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        {filteredPackages.map((pkg) => (
        <div 
          key={pkg.id} 
          className="animate-in fade-in slide-in-from-bottom-4 hover:-translate-y-1 transition-transform duration-300"
        >
          <PackageCard package={pkg} />
        </div>
        ))}
      </div>
      ) : (
      <NoPackagesFound />
      )}
    </section>
  )
}
